import { MockDataQuerySpec, TimeSeries, TimeSeriesValueTuple } from '@/core';
import { mockRealisticTimeSeriesData } from '@/internal-utils/utils/time-series/mockTimeSeriesData';
import { MockRealisticSeriesConfig } from '@/internal-utils/utils/time-series/model';
import {
  createDataQueryPlugin,
  DataQuery,
  DataQueryPlugin,
  GetDataResponse,
  QueryPluginContext,
} from '@/plugin-system';
import { MockDataQueryEditor } from './MockDataQueryEditor';

const createMockDataQuerySpec: DataQueryPlugin<MockDataQuerySpec>['createInitialOptions'] = () => {
  return {
    totalSeries: 1,
    totalDatapoints: 1000,
    seed: 42,
    yVariationMultiplier: 12,
    randomnessMultiplier: 50,
    outlierRatio: 0.06,
    outlierMultiplier: 1.05,
    sparseRatio: 1,
  };
};

/**
 * This is an example implementation of a basic data query plugin. It is NOT intended to be
 * used in user-facing code. Its purpose is to support the following:
 * - Provide a code-based example as a form of documentation, which is easier to create, maintain, and
 *   reference than markdown-based code in storybook.
 * - Provide a real plugin that can be used in internal tenants to simulate how the plugin works in
 *   a dashboard.
 * - Provide a plugin with consistent data that could be used for integration and/or e2e testing in
 *   in cloud. Testing against real plugins with real data can be challenging because the data is
 *   not guaranteed to be consistent.
 *
 * This is NOT intended to be a customer-facing plugin and is gated by the `enable-viz-framework-mocks`
 * feature flag.
 */
export class MockDataQueryPlugin extends DataQuery<MockDataQuerySpec> {
  async getData(spec: MockDataQuerySpec, ctx: QueryPluginContext): Promise<GetDataResponse> {
    const { start, end } = ctx.timeRange;
    const startTimeMs = start.getTime();
    const endTimeMs = end.getTime();
    const timeRangeMs = end.getTime() - start.getTime();
    const datapoints = 1000;
    const stepMs = timeRangeMs / datapoints;

    // TODO: return mock tabular data
    if (ctx.dataType === 'Table') {
      const mockTabularData: GetDataResponse = {
        type: 'Table',
        data: {
          data: {
            length: 0,
            fields: [],
          },
        },
      };
      return new Promise((resolve) => {
        setTimeout(() => resolve(mockTabularData), 1000);
      });
    }

    const mockTimeSeries = getMockTimeSeriesData({ startTimeMs, endTimeMs, ...spec });
    const mockTimeSeriesData: GetDataResponse = {
      type: 'TimeSeries',
      data: {
        timeRange: ctx.timeRange,
        stepMs,
        series: mockTimeSeries,
      },
    };

    return new Promise((resolve) => {
      setTimeout(() => resolve(mockTimeSeriesData), 1000);
    });
  }
}

export const MockDataQuery = createDataQueryPlugin<MockDataQuerySpec>({
  plugin: MockDataQueryPlugin,
  OptionsEditorComponent: MockDataQueryEditor,
  createInitialOptions: createMockDataQuerySpec,
});

const getMockTimeSeriesData = (config: MockRealisticSeriesConfig): TimeSeries[] => {
  const mockData = mockRealisticTimeSeriesData(config);

  return mockData.map(({ metric, values }) => {
    const name = metric.__name__;
    const valueTuples = values.map((valueTuple) => {
      const [unixTimeSeconds, val] = valueTuple;
      return [unixTimeSeconds * 1000, parseFloat(val)] as TimeSeriesValueTuple;
    });
    return { name, values: valueTuples };
  }) as TimeSeries[];
};
